import React from 'react';
import { Target, Award, TrendingUp, Loader, AlertCircle } from 'lucide-react';
import './EliteComparisonSection.css';

const EliteComparisonSection = ({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="section-loading">
        <div className="section-loading-spinner" />
        <p>Loading elite comparison...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-error">
        <AlertCircle className="section-error-icon" />
        <p className="section-error-message">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="section-no-data">
        <p>No elite standards available for this element type.</p>
      </div>
    );
  }

  // Safe number formatting helper
  const safeFixed = (value, decimals = 2) => {
    return (value != null && !isNaN(value)) ? Number(value).toFixed(decimals) : 'N/A';
  };

  // Format metric names for display
  const formatMetricName = (key) => {
    // Ensure key is a string
    if (typeof key !== 'string') {
      return String(key);
    }

    const nameMap = {
      jump_height_cm: 'Jump Height',
      rotation_speed_rev_s: 'Rotation Speed',
      rotation_count: 'Rotation Count',
      takeoff_velocity_m_s: 'Takeoff Velocity',
      landing_stability_score: 'Landing Stability',
      isu_score: 'ISU Score',
      air_time_s: 'Air Time'
    };
    return nameMap[key] || key.replace(/_/g, ' ');
  };

  // Format metric values with units
  const formatMetricValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A';
    
    const unitMap = {
      jump_height_cm: 'cm',
      rotation_speed_rev_s: 'rev/s',
      rotation_count: '',
      takeoff_velocity_m_s: 'm/s',
      landing_stability_score: '/100',
      isu_score: '',
      air_time_s: 's'
    };
    
    const unit = unitMap[key] || '';
    const formatted = (value != null && !isNaN(value)) ? Number(value).toFixed(2) : 'N/A';
    return `${formatted}${unit ? ' ' + unit : ''}`;
  };

  // Get percentile color
  const getPercentileColor = (percentile) => {
    if (percentile >= 80) return '#10b981'; // Green
    if (percentile >= 60) return '#3b82f6'; // Blue
    if (percentile >= 40) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  // Format target level for display
  const formatTargetLevel = (level) => {
    if (!level) return 'Unknown';
    // Ensure level is a string
    if (typeof level !== 'string') {
      return String(level);
    }

    const levelMap = {
      olympic_gold: 'Olympic Gold',
      olympic_podium: 'Olympic Podium',
      elite: 'Elite'
    };
    return levelMap[level] || level;
  };

  const percentile = data.overall_percentile || 0;
  const percentileColor = getPercentileColor(percentile);
  const achievements = data.achievements || [];
  const focusAreas = data.focus_areas || [];

  return (
    <div className="elite-comparison-section">
      {/* Target Level Badge */}
      <div className="target-level-badge">
        <Target className="target-icon" />
        <span>Comparing to: {formatTargetLevel(data.target_level)}</span>
      </div>

      {/* Percentile Circle */}
      <div className="percentile-container">
        <div className="percentile-circle" style={{ borderColor: percentileColor }}>
          <div className="percentile-value" style={{ color: percentileColor }}>
            {safeFixed(percentile, 0)}
          </div>
          <div className="percentile-label">Percentile</div>
        </div>
        <p className="percentile-description">
          You're performing at the {safeFixed(percentile, 0)}th percentile compared to {formatTargetLevel(data.target_level)} standards
        </p>
      </div>

      {/* Achievements */}
      {achievements.length > 0 && (
        <div className="achievements-section">
          <h5 className="section-subtitle">
            <Award className="subtitle-icon" />
            Achievements
          </h5>
          <div className="achievements-list">
            {achievements.map((achievement, index) => (
              <div key={index} className="achievement-item">
                <span className="achievement-badge">✅</span>
                <div className="achievement-content">
                  <span className="achievement-metric">{formatMetricName(achievement.metric)}</span>
                  <span className="achievement-message">{achievement.message}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Focus Areas */}
      {focusAreas.length > 0 && (
        <div className="focus-areas-section">
          <h5 className="section-subtitle">
            <TrendingUp className="subtitle-icon" />
            Focus Areas
          </h5>
          <div className="focus-areas-list">
            {focusAreas.map((area, index) => (
              <div key={index} className="focus-area-item">
                <span className="focus-badge">🎯</span>
                <div className="focus-content">
                  <div className="focus-header">
                    <span className="focus-metric">{formatMetricName(area.metric)}</span>
                    <span className="focus-gap">{safeFixed(area.gap_percentage || area.gap_pct, 1)}% below elite</span>
                  </div>
                  <div className="focus-details">
                    <span className="focus-label">Current:</span>
                    <span className="focus-value">{formatMetricValue(area.metric, area.current)}</span>
                    <span className="focus-divider">→</span>
                    <span className="focus-label">Target:</span>
                    <span className="focus-value">{formatMetricValue(area.metric, area.target)}</span>
                  </div>
                  <p className="focus-message">{area.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {data.summary && (
        <div className="summary-section">
          <p className="summary-text">{data.summary}</p>
        </div>
      )}

      {/* No achievements or focus areas */}
      {achievements.length === 0 && focusAreas.length === 0 && (
        <div className="no-data-message">
          <p>Complete more attempts to see detailed comparisons with elite standards.</p>
        </div>
      )}
    </div>
  );
};

export default EliteComparisonSection;
